import type { PluginImportConfig } from '@squaredup/plugins';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import { usePluginConfigMappingStep } from 'pages/datasources/components/customImport/usePluginConfigMappingStep';
import { usePluginImportDataStreamStep } from 'pages/datasources/components/customImport/usePluginImportDataStreamStep';
import { useEffect, useState } from 'react';
import { useDatasetContext } from 'ui/editor/dataStream/contexts/DatasetContext';
import { EditorSteps } from 'ui/editor/dataStream/TileEditor/constants';
import TileEditorStepsContext from 'ui/editor/dataStream/TileEditor/contexts/TileEditorStepsContext';
import { useDataStreamFilters } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamFilters';
import { useDataStreamSupportedTimeframes } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamSupportedTimeframes';
import { useDataStreamType } from 'ui/editor/dataStream/TileEditor/hooks/useDataStreamType';
import { StepWithCondition, type TileEditorStepsValue } from 'ui/editor/dataStream/TileEditor/hooks/useTileEditorSteps';
import { useTileEditorStore } from 'ui/editor/dataStream/TileEditor/state/TileEditorStoreProvider';
import { useParametersStep } from 'ui/editor/dataStream/TileEditor/steps/stepDefinitions/dataStreamConfigurationStep/useParametersStep';
import { useMetadataStep } from 'ui/editor/dataStream/TileEditor/steps/stepDefinitions/metadataStep/useMetadataStep';
import { useShapingStep } from 'ui/editor/dataStream/TileEditor/steps/stepDefinitions/shapingStep/useShapingStep';
import { useTimeframeStep } from 'ui/editor/dataStream/TileEditor/steps/stepDefinitions/timeframeStep/useTimeframeStep';
import { usePluginImportConfigEditorContext } from './PluginImportConfigEditorContext';
import { usePluginImportObjectsStep } from './usePluginImportObjectsStep';

interface PluginImportConfigStepsContextWrapperProps {
    initialStep?: EditorSteps;
    dataSourceId: string;
}

export const PluginImportConfigStepsContextWrapper: React.FC<PluginImportConfigStepsContextWrapperProps> = ({
    initialStep,
    dataSourceId,
    children
}) => {
    const { config: datasetConfig, setConfig: setDatasetConfig } = useDatasetContext();
    const { data: datasetData, isConfigured } = useDataStreamConfig(datasetConfig);
    const supportedTimeframes = useDataStreamSupportedTimeframes(datasetConfig.dataStream?.id);
    const { dataStreamType, isLoading } = useDataStreamType(datasetConfig);

    const { dataStreamState, scope, storeCurrentStep, workspaceId, dispatch } = useTileEditorStore((s) => ({
        storeCurrentStep: s.currentStep,
        dataStreamState: s.dataStream,
        scope: s.scope,
        workspaceId: s.dashboard.workspaceId,
        dispatch: s.dispatch
    }));

    const [previousDataStreamId, setPreviousDataStreamId] = useState<string | undefined>(
        dataStreamState.selectedDataStream?.id
    );
    const { setConfig, configId, tileConfig, ...mappingProps } = usePluginImportConfigEditorContext();

    useEffect(() => {
        // Reset the mapping fields when the selected data stream changes.
        // This prevents the user saving the old mapping values which are likely now invalid.
        if (dataStreamState.selectedDataStream?.id !== previousDataStreamId) {
            setPreviousDataStreamId(dataStreamState.selectedDataStream?.id);
            setConfig(
                (prev): PluginImportConfig => ({
                    ...prev,
                    sourceNameColumn: undefined,
                    sourceIdColumn: undefined,
                    sourceType: {
                        mode: 'fixed',
                        value: undefined,
                        column: undefined
                    },
                    columnMapping: {}
                })
            );
        }
    }, [dataStreamState.selectedDataStream?.id, mappingProps, previousDataStreamId, setConfig]);

    const dataStreamFilters = useDataStreamFilters({
        dispatch,
        ...dataStreamState,
        filters: {
            ...dataStreamState.filters,
            dataSourceId: dataSourceId
        }
    });

    const hasDataStream = Boolean(datasetConfig.dataStream);
    const isConfigurableDataStream = dataStreamType && dataStreamType.isConfigurable;

    const hasScope = Object.keys(datasetConfig.scope || {}).length > 0;

    const editorSteps: StepWithCondition<any, any>[] = [
        usePluginImportDataStreamStep({
            isLoading,
            dataStreamFilters,
            pluginId: dataSourceId
        }),
        usePluginImportObjectsStep({
            hasDataStream,
            storeCurrentStep,
            datasetConfigScope: datasetConfig.scope,
            scopeState: scope,
            workspaceId
        }),
        useParametersStep({
            isLoading,
            hasDataStream,
            isConfigurableDataStream,
            hasScope,
            supportsScope: dataStreamType.supportsScope
        }),
        useTimeframeStep({
            isLoading,
            hasDataStream,
            isConfigured,
            dataStreamId: datasetConfig.dataStream?.id,
            inheritedTimeframe: 'last24hours'
        }),
        useShapingStep({
            isLoading,
            hasDataStream,
            isConfigured
        }),
        useMetadataStep({
            isLoading,
            hasDataStream,
            isConfigured,
            dataStreamMetadata: datasetConfig.dataStream?.metadata,
            columnNames: datasetData.metadata.columns.map((column) => column.name),
            config: datasetConfig,
            setConfig: setDatasetConfig
        }),
        usePluginConfigMappingStep({
            isLoading,
            hasDataStream,
            isConfigured
        })
    ];

    const [currentStepName, setCurrentStepName] = useState(initialStep);

    const availableSteps = editorSteps.filter((step) => step.isEnabled);
    const stepInFlow = availableSteps.findIndex((step) => step.name === currentStepName);

    const canMoveToNextStep = stepInFlow < availableSteps.length - 1;

    const nextStep = () => {
        if (canMoveToNextStep) {
            setCurrentStepName(availableSteps[stepInFlow + 1]?.name);
        }
    };

    const handleSetEditorStepName = (stepName?: EditorSteps | undefined, force = false) => {
        if (force) {
            // If we're forcing a step we don't want to do anything other than set the current editor step
            return setCurrentStepName(stepName);
        }
        const newCurrentStep = editorSteps.find((s) => s.name === stepName);

        if (newCurrentStep && newCurrentStep?.isEnabled === false) {
            return;
        }

        return setCurrentStepName(newCurrentStep?.name);
    };

    const stepsValue: TileEditorStepsValue = {
        currentEditorStep: currentStepName,
        isConfiguredAfterCurrentStep: false,
        editorSteps: availableSteps,
        canMoveToNextStep,
        isConfigured: false,
        isDocked: true,
        canDock: false,
        setCurrentEditorStep: handleSetEditorStepName,
        nextStep
    };

    return <TileEditorStepsContext.Provider value={stepsValue}>{children}</TileEditorStepsContext.Provider>;
};
